import {
  ChatMessage,
  ChatSource,
  ChatArtifact,
  ChatAction,
  ChatActionRequest,
  SrCitation,
} from "./models/chatBotModels";

/** Max citation chips to render per message (bot-side cap mirrored defensively in FE) */
const MAX_SR_CITATIONS = 5;

// ============================================================
// SSE Streaming Utility — shared between full MIDAS (Redux)
// and kiosk chat (local state). No Redux dependency.
// ============================================================

/** Map SSE stage names to i18n step keys used by ChatTypingIndicator */
const sseStageToStepKey: Record<string, string> = {
  classifying: "steps.analyzing",
  analyzing: "steps.analyzing",
  searching: "steps.searching",
  generating: "steps.generating",
};

/** Generate a unique message ID */
const generateId = (): string => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
};

/**
 * Parse raw SSE text into individual events.
 * SSE events are separated by double newlines.
 */
export const parseSSEEvents = (
  raw: string
): Array<{ event: string; data: string }> => {
  const events: Array<{ event: string; data: string }> = [];
  const blocks = raw.split(/\r?\n\r?\n/).filter((b) => b.trim().length > 0);
  for (const block of blocks) {
    let eventType = "message";
    const dataLines: string[] = [];
    const lines = block.split(/\r?\n/);
    for (const line of lines) {
      if (line.startsWith("event:")) {
        eventType = line.slice(6).trim();
      } else if (line.startsWith("data:")) {
        dataLines.push(line.slice(5).trim());
      }
    }
    if (dataLines.length > 0) {
      events.push({ event: eventType, data: dataLines.join("\n") });
    }
  }
  return events;
};

/**
 * Normalize a bot image URL to a gateway-relative path.
 * The bot may send either:
 *   - A relative path: /api/v1/image/SINUMERIK_.../page_042.jpg
 *   - A full URL:      https://machineid.chat/api/v1/image/SINUMERIK_.../page_042.jpg
 * This function strips any origin prefix to produce a consistent relative path.
 */
export const normalizeBotImageUrl = (url: string): string => {
  if (!url) return url;
  // Already a relative path — return as-is
  if (url.startsWith("/")) return url;
  // Full URL — extract the path portion starting from /api/v1/image/
  const imagePathIndex = url.indexOf("/api/v1/image/");
  if (imagePathIndex !== -1) {
    return url.slice(imagePathIndex);
  }
  // Other full URL patterns — try to extract the path
  try {
    const parsed = new URL(url);
    return parsed.pathname;
  } catch {
    return url;
  }
};

/** Check if a URL is a valid bot image URL (relative or full) */
export const isBotImageUrl = (url: string): boolean => {
  if (!url || typeof url !== "string") return false;
  // Only accept known bot image/diagram API paths — not arbitrary relative URLs
  if (url.startsWith("/api/v1/image/")) return true;
  if (url.startsWith("/api/v1/diagram/")) return true;
  // Accept full URLs that contain /api/v1/image/ path
  if (url.includes("/api/v1/image/")) return true;
  return false;
};

/** Map bot source objects from snake_case to camelCase */
export const mapBotSources = (sources?: any[]): ChatSource[] | undefined => {
  if (!sources || sources.length === 0) return undefined;
  return sources.map((s: any) => {
    const rawImageUrl = s.image_url ?? s.imageUrl;
    return {
      document: s.document,
      page: s.page,
      pageFrom: s.page_from ?? s.pageFrom,
      pageTo: s.page_to ?? s.pageTo,
      url: s.url,
      // Normalize to relative path so downstream code can safely check startsWith("/")
      imageUrl: rawImageUrl ? normalizeBotImageUrl(rawImageUrl) : undefined,
    };
  });
};

/** Map bot artifact objects from SSE answer payload */
export const mapBotArtifacts = (
  artifacts?: any[]
): ChatArtifact[] | undefined => {
  if (!artifacts || artifacts.length === 0) return undefined;
  const RICH_TYPES = ["table", "machine_card", "sr_summary", "diagram_overlay"];
  // Rich types that require a non-empty data field to be useful
  const DATA_REQUIRED_TYPES = ["table", "machine_card", "sr_summary"];
  return artifacts
    .filter((a: any) => {
      if (!a) return false;
      if (RICH_TYPES.includes(a.type)) {
        // Reject rich artifacts (except diagram_overlay) with missing/empty data
        if (DATA_REQUIRED_TYPES.includes(a.type)) {
          if (!a.data || (typeof a.data === "object" && Object.keys(a.data).length === 0)) {
            return false;
          }
        }
        return true;
      }
      // user_image (P2) and image artifacts: must carry a valid bot image URL
      // Accept both relative paths (/api/v1/image/...) and full bot URLs
      return a.url && typeof a.url === "string" && isBotImageUrl(a.url);
    })
    .map((a: any) => ({
      type: a.type || "image",
      title: a.title || "",
      // Preserve "user" source for user_image artifacts (P2);
      // MC-25: preserve "cross_reference" source for list-ref artifacts;
      // default bot-side to "bot"
      source:
        a.source === "user"
          ? "user"
          : a.source === "cross_reference"
          ? "cross_reference"
          : (a.source || "bot"),
      // Normalize URL to relative path for consistent downstream handling
      url: normalizeBotImageUrl(a.url || ""),
      document_id: a.document_id,
      page: a.page,
      mime_type: a.mime_type,
      entity_type: a.entity_type,
      entity_id: a.entity_id,
      file_id: a.file_id,
      data: a.data,
    }));
};

/**
 * Map service-report citations from SSE answer payload (MC-22).
 * - snake_case → camelCase
 * - drops malformed entries (missing report_number or non-numeric citation_count)
 * - caps at MAX_SR_CITATIONS (bot already caps; we enforce again defensively)
 * - preserves input order (bot pre-sorts desc by citation_count)
 * - returns undefined when input is missing/empty so downstream conditionals work
 */
export const mapSrCitations = (arr?: any[]): SrCitation[] | undefined => {
  if (!Array.isArray(arr) || arr.length === 0) return undefined;
  const mapped: SrCitation[] = [];
  for (const entry of arr) {
    if (!entry || typeof entry !== "object") continue;
    const reportNumber = entry.report_number ?? entry.reportNumber;
    const rawCount = entry.citation_count ?? entry.citationCount;
    if (typeof reportNumber !== "string" || reportNumber.trim().length === 0) continue;
    // Default missing count to 1; coerce numeric strings; reject NaN
    const count = typeof rawCount === "number" ? rawCount : Number(rawCount);
    if (!Number.isFinite(count) || count < 1) {
      mapped.push({ reportNumber, citationCount: 1 });
    } else {
      mapped.push({ reportNumber, citationCount: Math.floor(count) });
    }
    if (mapped.length >= MAX_SR_CITATIONS) break;
  }
  return mapped.length > 0 ? mapped : undefined;
};

/** Map bot action objects from SSE answer payload */
export const mapBotActions = (actions?: any[]): ChatAction[] | undefined => {
  if (!actions || actions.length === 0) return undefined;
  return actions
    .filter((a: any) => a && a.id && a.label)
    .map((a: any) => ({
      id: a.id,
      label: a.label,
      style: a.style || "secondary",
      context: a.context,
    }));
};

/**
 * Generate a human-readable fallback title from an action_type string.
 * E.g. "machinery.create_service_report" → "Create Service Report"
 */
const fallbackTitleFromActionType = (actionType: string): string => {
  const lastSegment = actionType.includes(".")
    ? actionType.slice(actionType.lastIndexOf(".") + 1)
    : actionType;
  return lastSegment
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

/**
 * SEC-REQ-8: Validate an action_request SSE payload.
 * Returns a validated ChatActionRequest or null if malformed.
 */
export const validateActionRequest = (data: any): ChatActionRequest | null => {
  if (!data || typeof data !== "object") return null;
  if (typeof data.action_id !== "string" || data.action_id.length === 0) return null;
  if (typeof data.action_type !== "string" || data.action_type.length === 0) return null;
  // display is optional — generate fallback title from action_type if missing
  // Schema is optional for MA-0a (hardcoded schemas used instead)
  // but if present, fields must be an array
  if (data.schema && data.schema.fields && !Array.isArray(data.schema.fields)) return null;

  const hasDisplayTitle = data.display && typeof data.display.title === "string" && data.display.title.length > 0;
  // label is more reliably sent by the bot for nav actions — use as fallback for display.title
  const label = typeof data.label === "string" && data.label.length > 0 ? data.label : undefined;
  const renderHint = typeof data.render_hint === "string" && data.render_hint.length > 0 ? data.render_hint : undefined;

  // BUG10-8: Inject top-level created_at into values so the executor and form
  // can read it as values.created_at. The bot sends created_at as a sibling of
  // action_type/action_id/values, not inside values.
  const mergedValues = { ...(data.values || {}) };
  if (data.created_at && typeof data.created_at === "string" && !mergedValues.created_at) {
    mergedValues.created_at = data.created_at;
  }

  return {
    action_id: data.action_id,
    action_type: data.action_type,
    schema: data.schema || { fields: [] },
    values: mergedValues,
    display: {
      title: hasDisplayTitle ? data.display.title : (label || fallbackTitleFromActionType(data.action_type)),
      summary: data.display?.summary,
      icon: data.display?.icon,
    },
    approval_tier: typeof data.approval_tier === "number" ? data.approval_tier : 1,
    label,
    render_hint: renderHint,
  };
};

// ============================================================
// MC-25: <list-ref> tag parsing
// ============================================================

/** A single parsed <list-ref> tag occurrence. */
export interface ListRefSegment {
  kind: "list-ref";
  /** Tag's `id` attribute (e.g. "050") — supplementary display only. */
  listId: string;
  /** Tag's `page` attribute — canonical join key to a `cross_reference` artifact. */
  page: number;
  /** Tag's optional `name` attribute (e.g. "Vstupny modul 1"). */
  name?: string;
  /** Visible text between the tags (e.g. "List 050"). */
  label: string;
}

/** A plain text run with no embedded tag. */
export interface TextSegment {
  kind: "text";
  text: string;
}

export type MessageSegment = TextSegment | ListRefSegment;

/** Matches a complete <list-ref ...>label</list-ref> tag with any attribute order. */
const LIST_REF_TAG_RE = /<list-ref\b([^>]*)>([\s\S]*?)<\/list-ref>/gi;

/** Extract a quoted or unquoted attribute from an attribute-string blob. */
const readAttr = (attrs: string, name: string): string | undefined => {
  const re = new RegExp(`\\b${name}\\s*=\\s*(?:"([^"]*)"|'([^']*)'|([^\\s>]+))`, "i");
  const m = attrs.match(re);
  if (!m) return undefined;
  return m[1] ?? m[2] ?? m[3];
};

/**
 * MC-25: Parse a (render-safe) bot message string into alternating text and
 * list-ref segments. The caller is expected to guarantee the input contains
 * no half-open <list-ref tags — use `trimUnclosedListRefTail` to split an
 * incoming stream buffer into a safe prefix + unsafe tail first.
 */
export const parseListRefs = (text: string): MessageSegment[] => {
  if (!text) return [];
  const out: MessageSegment[] = [];
  let lastIndex = 0;
  LIST_REF_TAG_RE.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = LIST_REF_TAG_RE.exec(text)) !== null) {
    if (m.index > lastIndex) {
      out.push({ kind: "text", text: text.slice(lastIndex, m.index) });
    }
    const attrs = m[1] || "";
    const label = (m[2] || "").trim();
    const pageStr = readAttr(attrs, "page");
    const listId = readAttr(attrs, "id") ?? "";
    const name = readAttr(attrs, "name");
    const page = pageStr !== undefined ? Number(pageStr) : NaN;
    if (Number.isFinite(page) && label) {
      out.push({ kind: "list-ref", listId, page, name, label });
    } else {
      // Malformed tag — render as plain text so the user still sees the label
      out.push({ kind: "text", text: m[0] });
    }
    lastIndex = m.index + m[0].length;
  }
  if (lastIndex < text.length) {
    out.push({ kind: "text", text: text.slice(lastIndex) });
  }
  return out;
};

/**
 * MC-25 streaming safety: split an in-flight buffer into a "render-safe" prefix
 * (no half-open tag) and an "unsafe tail" that should be kept buffered until
 * the rest of the chunk arrives.
 *
 * An SSE token chunk may split mid-tag, e.g. `"See List <list-r"` | `"ef id=\"050\" ...>List 050</list-ref>"`.
 * If we rendered the prefix as-is we'd flicker a literal `<list-r` into the DOM.
 *
 * Rules:
 *  - An opening `<list-ref` without a matching `</list-ref>` anywhere later in
 *    the buffer is unsafe — hold everything from that `<` onwards.
 *  - A bare `<` or `<l`, `<li`, `<lis`, `<list`, `<list-`, `<list-r`, `<list-re`,
 *    `<list-ref` prefix at the end is also unsafe (user hasn't seen enough
 *    characters to even tell it's a tag yet) — hold it back.
 *  - Otherwise, the whole buffer is safe to render.
 */
export const trimUnclosedListRefTail = (
  buffer: string
): { safe: string; tail: string } => {
  if (!buffer) return { safe: "", tail: "" };
  // 1. Find the last `<` — if there's no `<` at all, everything is safe.
  const lastLt = buffer.lastIndexOf("<");
  if (lastLt === -1) return { safe: buffer, tail: "" };

  // 2. Examine the suffix starting at `lastLt`.
  const suffix = buffer.slice(lastLt);

  // Fast path: suffix contains a complete closing tag — everything is safe.
  // (A stray `<` followed by `</list-ref>` later would have closed an earlier
  // open tag, so trust the regex-friendly form here.)
  if (/<\/list-ref\s*>/i.test(suffix)) {
    return { safe: buffer, tail: "" };
  }

  // Is the suffix an "opener" that hasn't closed yet?
  // Cases to hold back:
  //   a) `<list-ref ...>` without `</list-ref>` in the suffix → hold from `<`
  //   b) A prefix of the literal string `<list-ref` → hold from `<`
  const openerRe = /^<list-ref\b/i;
  if (openerRe.test(suffix)) {
    // (a) — opener is present but no closing tag yet → buffer the whole opener
    return { safe: buffer.slice(0, lastLt), tail: suffix };
  }

  // (b) — could still be building up the opener character-by-character.
  // Everything up to and including `<list-ref` (9 chars) is possibly the
  // beginning of a list-ref tag.
  const LIST_REF_PREFIX = "<list-ref";
  if (suffix.length <= LIST_REF_PREFIX.length) {
    const lower = suffix.toLowerCase();
    if (LIST_REF_PREFIX.startsWith(lower)) {
      return { safe: buffer.slice(0, lastLt), tail: suffix };
    }
  }

  // Any other `<` (e.g. `<br`, `<foo>`) is not our concern — render it.
  return { safe: buffer, tail: "" };
};

/** Callbacks for SSE stream processing */
export interface StreamCallbacks {
  onStep: (stepKey: string) => void;
  onMessage: (message: ChatMessage) => void;
  onError: (error: string) => void;
  /** Called when an action_request SSE event is received (MA-0) */
  onActionRequest?: (actionRequest: ChatActionRequest) => void;
  /** Called when a token SSE event is received (progressive text rendering) */
  onToken?: (text: string) => void;
  /** Called when thread_id is found in the answer event (MIDAS-THREADSYNC Phase 2) */
  onThreadId?: (threadId: string) => void;
  /** Called when thread_created SSE event is received (early thread reconciliation) */
  onThreadCreated?: (threadId: string) => void;
}

/**
 * Read an SSE response stream and invoke callbacks for progress and answer.
 * Returns the final bot message, or null if none received.
 * Includes a 90-second timeout to prevent indefinite hanging.
 */
export const readSSEStream = async (
  response: Response,
  callbacks: StreamCallbacks
): Promise<ChatMessage | null> => {
  const reader = response.body?.getReader();
  if (!reader) {
    callbacks.onError("Stream body is not readable");
    return null;
  }

  const decoder = new TextDecoder();
  let buffer = "";
  let botMessage: ChatMessage | null = null;
  const STREAM_READ_TIMEOUT = 90_000; // 90 seconds

  try {
    while (true) {
      // Race each read against a timeout to prevent indefinite hanging
      let timeoutId: ReturnType<typeof setTimeout> | undefined;
      const readPromise = reader.read();
      const timeoutPromise = new Promise<{ done: true; value: undefined }>((resolve) => {
        timeoutId = setTimeout(() => {
          // Treat timeout as "stream ended" — the catch block below won't fire,
          // but botMessage will be null → triggers sync fallback in the caller
          resolve({ done: true, value: undefined });
        }, STREAM_READ_TIMEOUT);
      });
      const { done, value } = await Promise.race([readPromise, timeoutPromise]);
      clearTimeout(timeoutId);
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // Find the last SSE block delimiter.
      // Standard SSE uses "\n\n", but some servers send "\r\n\r\n".
      // Check for both patterns and use whichever appears last.
      const lf2 = buffer.lastIndexOf("\n\n");
      const crlf2 = buffer.lastIndexOf("\r\n\r\n");
      // Pick the later occurrence; for \r\n\r\n the "end" is +4 chars, for \n\n it's +2
      let splitPos = -1;
      let delimLen = 0;
      if (lf2 >= 0 && crlf2 >= 0) {
        // Both found — use the later one
        if (crlf2 >= lf2) {
          splitPos = crlf2;
          delimLen = 4;
        } else {
          splitPos = lf2;
          delimLen = 2;
        }
      } else if (crlf2 >= 0) {
        splitPos = crlf2;
        delimLen = 4;
      } else if (lf2 >= 0) {
        splitPos = lf2;
        delimLen = 2;
      }
      if (splitPos === -1) continue;

      const completePart = buffer.slice(0, splitPos + delimLen);
      buffer = buffer.slice(splitPos + delimLen);

      const events = parseSSEEvents(completePart);

      for (const evt of events) {
        if (evt.event === "status") {
          try {
            const statusData = JSON.parse(evt.data);
            const stepKey = sseStageToStepKey[statusData.stage];
            if (stepKey) {
              callbacks.onStep(stepKey);
            }
          } catch {
            // Ignore malformed status events
          }
        } else if (evt.event === "thread_created") {
          // Early thread reconciliation — fires before any tokens when gateway auto-creates a thread
          try {
            const data = JSON.parse(evt.data);
            if (data.thread_id && typeof data.thread_id === "string" && callbacks.onThreadCreated) {
              callbacks.onThreadCreated(data.thread_id);
            }
          } catch {
            // Ignore malformed thread_created events
          }
        } else if (evt.event === "token") {
          // Progressive text rendering — append token text to streaming output
          try {
            const tokenData = JSON.parse(evt.data);
            if (tokenData.text && typeof tokenData.text === "string" && callbacks.onToken) {
              callbacks.onToken(tokenData.text);
            }
          } catch {
            // Ignore malformed token events
          }
        } else if (evt.event === "answer") {
          try {
            const answerData = JSON.parse(evt.data);
            botMessage = {
              id: generateId(),
              role: "assistant",
              text: answerData.message,
              timestamp: new Date().toISOString(),
              sources: mapBotSources(answerData.sources),
              suggestions: answerData.suggestions,
              intent: answerData.metadata?.intent,
              artifacts: mapBotArtifacts(answerData.artifacts),
              actions: mapBotActions(answerData.actions),
              srCitations: mapSrCitations(answerData.sr_citations),
            };
            callbacks.onMessage(botMessage);
            // MIDAS-THREADSYNC Phase 2: Extract thread_id from answer event
            if (answerData.thread_id && typeof answerData.thread_id === "string" && callbacks.onThreadId) {
              callbacks.onThreadId(answerData.thread_id);
            }
          } catch {
            // Ignore malformed answer events
          }
        } else if (evt.event === "done") {
          // MC-22: some bot versions emit a separate "done" event after "answer"
          // carrying sr_citations. Merge into the last emitted message if not
          // already set on the answer payload.
          try {
            const doneData = JSON.parse(evt.data);
            const citations = mapSrCitations(doneData.sr_citations);
            if (citations && botMessage && !botMessage.srCitations) {
              botMessage.srCitations = citations;
              callbacks.onMessage(botMessage);
            }
          } catch {
            // Ignore malformed done events
          }
        } else if (evt.event === "action_request") {
          // MA-0: Parse action_request SSE event (SEC-REQ-8: strict validation)
          try {
            const requestData = JSON.parse(evt.data);
            const validated = validateActionRequest(requestData);
            if (validated && callbacks.onActionRequest) {
              callbacks.onActionRequest(validated);
            } else if (!validated) {
              console.warn("[SSE] Malformed action_request payload — rejected (SEC-REQ-8)");
            }
          } catch {
            console.warn("[SSE] Failed to parse action_request event data");
          }
        } else if (evt.event === "error") {
          try {
            const errorData = JSON.parse(evt.data);
            callbacks.onError(
              errorData.message || "An error occurred while processing"
            );
            return null;
          } catch {
            callbacks.onError("An error occurred while processing");
            return null;
          }
        } else if (evt.event !== "message") {
          // Unrecognized event type — try to extract message if JSON
          try {
            const data = JSON.parse(evt.data);
            if (data.message) {
              botMessage = {
                id: generateId(),
                role: "assistant",
                text: data.message,
                timestamp: new Date().toISOString(),
                sources: mapBotSources(data.sources),
                suggestions: data.suggestions,
                intent: data.metadata?.intent,
                artifacts: mapBotArtifacts(data.artifacts),
                actions: mapBotActions(data.actions),
                srCitations: mapSrCitations(data.sr_citations),
              };
              callbacks.onMessage(botMessage);
            }
          } catch {
            // Not JSON or no message — skip
          }
        }
      }
    }

    // Process any remaining buffer content
    if (buffer.trim().length > 0) {
      const events = parseSSEEvents(buffer);
      for (const evt of events) {
        if (evt.event === "thread_created") {
          // Early thread reconciliation — remaining buffer path
          try {
            const data = JSON.parse(evt.data);
            if (data.thread_id && typeof data.thread_id === "string" && callbacks.onThreadCreated) {
              callbacks.onThreadCreated(data.thread_id);
            }
          } catch {
            // Ignore malformed thread_created events
          }
        } else if (evt.event === "token") {
          // Handle token events in remaining buffer
          try {
            const tokenData = JSON.parse(evt.data);
            if (tokenData.text && typeof tokenData.text === "string" && callbacks.onToken) {
              callbacks.onToken(tokenData.text);
            }
          } catch {
            // Ignore malformed token events
          }
        } else if (evt.event === "answer") {
          try {
            const answerData = JSON.parse(evt.data);
            botMessage = {
              id: generateId(),
              role: "assistant",
              text: answerData.message,
              timestamp: new Date().toISOString(),
              sources: mapBotSources(answerData.sources),
              suggestions: answerData.suggestions,
              intent: answerData.metadata?.intent,
              artifacts: mapBotArtifacts(answerData.artifacts),
              actions: mapBotActions(answerData.actions),
              srCitations: mapSrCitations(answerData.sr_citations),
            };
            callbacks.onMessage(botMessage);
            // MIDAS-THREADSYNC Phase 2: Extract thread_id from answer event (remaining buffer)
            if (answerData.thread_id && typeof answerData.thread_id === "string" && callbacks.onThreadId) {
              callbacks.onThreadId(answerData.thread_id);
            }
          } catch {
            // Ignore
          }
        } else if (evt.event === "done") {
          // MC-22: remaining-buffer path for separate "done" event
          try {
            const doneData = JSON.parse(evt.data);
            const citations = mapSrCitations(doneData.sr_citations);
            if (citations && botMessage && !botMessage.srCitations) {
              botMessage.srCitations = citations;
              callbacks.onMessage(botMessage);
            }
          } catch {
            // Ignore
          }
        } else if (evt.event === "action_request") {
          // MA-0: Parse action_request SSE event in remaining buffer
          try {
            const requestData = JSON.parse(evt.data);
            const validated = validateActionRequest(requestData);
            if (validated && callbacks.onActionRequest) {
              callbacks.onActionRequest(validated);
            }
          } catch {
            // Ignore malformed
          }
        } else if (evt.event === "error") {
          try {
            const errorData = JSON.parse(evt.data);
            callbacks.onError(
              errorData.message || "An error occurred while processing"
            );
            return null;
          } catch {
            callbacks.onError("An error occurred while processing");
            return null;
          }
        } else if (evt.event !== "message") {
          // Unrecognized event type — try to extract message if JSON
          try {
            const data = JSON.parse(evt.data);
            if (data.message) {
              botMessage = {
                id: generateId(),
                role: "assistant",
                text: data.message,
                timestamp: new Date().toISOString(),
                sources: mapBotSources(data.sources),
                suggestions: data.suggestions,
                intent: data.metadata?.intent,
                artifacts: mapBotArtifacts(data.artifacts),
                actions: mapBotActions(data.actions),
                srCitations: mapSrCitations(data.sr_citations),
              };
              callbacks.onMessage(botMessage);
            }
          } catch {
            // Not JSON or no message — skip
          }
        }
      }
    }
  } catch (err) {
    const errMsg = err instanceof Error ? err.message : "Stream processing failed";
    callbacks.onError(errMsg);
    return null;
  } finally {
    try {
      reader.releaseLock();
    } catch {
      // Reader may already be released
    }
  }

  return botMessage;
};
